import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { AuthShell } from "@/pages/Login";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements, { passwordIsStrong } from "@/components/PasswordRequirements";

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return err?.message || "Something went wrong";
}

export default function Register() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const strong = passwordIsStrong(password);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (!strong) {
      toast.error("Password doesn't meet the requirements below");
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim());
      toast.success("Account created — welcome");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-black tracking-tight">Create your account</h1>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
        Free tier includes 10 projects. Upgrade anytime.
      </p>

      <Button
        type="button"
        variant="outline"
        onClick={loginWithGoogle}
        className="w-full h-11 mt-6 rounded-md border-slate-300 hover:border-slate-950 text-slate-800 font-medium"
        data-testid="register-google-btn"
      >
        <GoogleLogo size={16} weight="bold" className="mr-2" />
        Sign up with Google
      </Button>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-sf-label">or with email</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="register-name"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="register-email"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">Password</Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a strong password"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="register-password"
          />
          <PasswordRequirements password={password} testid="register-password-requirements" />
        </div>
        <Button
          type="submit"
          disabled={loading || !strong || !email.trim()}
          className="w-full h-11 bg-slate-950 hover:bg-slate-800 text-white rounded-md font-medium"
          data-testid="register-submit"
        >
          {loading ? "Creating…" : (
            <span className="flex items-center gap-2 justify-center">
              Create Account <ArrowRight size={14} weight="bold" />
            </span>
          )}
        </Button>
      </form>

      <p className="mt-6 text-sm text-slate-500 text-center">
        Already have an account?{" "}
        <Link to="/login" className="text-slate-950 font-semibold hover:underline" data-testid="go-to-login">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}
